import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  Cell,
  LabelList,
} from 'recharts';
import {
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Flame,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import { useCitasSemana } from '@/components/hooks/useCitasSemana';
import { useChartColors, useDarkMode, useIsMobile } from './chartHooks';

interface Props {
  empresaId: string;
}

const hoyISO = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const aFecha = (iso: string) => new Date(`${iso}T00:00:00`);

const rangoTexto = (lunes: string) => {
  if (!lunes) return '';
  const ini = aFecha(lunes);
  const fin = new Date(ini);
  fin.setDate(ini.getDate() + 6);
  const mesCorto = (d: Date) =>
    d.toLocaleDateString('es-ES', { month: 'short' }).replace('.', '');
  return ini.getMonth() === fin.getMonth()
    ? `${ini.getDate()}–${fin.getDate()} ${mesCorto(fin)}`
    : `${ini.getDate()} ${mesCorto(ini)} – ${fin.getDate()} ${mesCorto(fin)}`;
};

const Chip = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-gray-100/80 dark:bg-white/5 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">
    {icon}
    {children}
  </span>
);

const BotonNav = ({
  onClick,
  children,
  label,
}: {
  onClick: () => void;
  children: React.ReactNode;
  label: string;
}) => (
  <button
    onClick={onClick}
    aria-label={label}
    className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white transition-colors"
  >
    {children}
  </button>
);

const DashboardSemana = ({ empresaId }: Props) => {
  const { t } = useTranslation();
  const dark = useDarkMode();
  const isMobile = useIsMobile();
  const colors = useChartColors(dark);
  const [offset, setOffset] = useState(0);
  const { loading, dias, lunes, totalSemanaAnterior } = useCitasSemana(
    empresaId,
    offset
  );

  const DIAS: Record<string, string> = {
    L: t('dashboardCharts.days.L'),
    M: t('dashboardCharts.days.M'),
    X: t('dashboardCharts.days.X'),
    J: t('dashboardCharts.days.J'),
    V: t('dashboardCharts.days.V'),
    S: t('dashboardCharts.days.S'),
    D: t('dashboardCharts.days.D'),
  };

  const semana = useMemo(() => {
    const hoy = hoyISO();
    const datos = dias.map((c) => {
      const fecha = String(c.fecha ?? '').slice(0, 10);
      return {
        dia: c.dia,
        fecha,
        numero: fecha ? Number(fecha.slice(8, 10)) : null,
        valor: Number(c.total_citas ?? 0),
        esHoy: fecha === hoy,
        pasado: fecha !== '' && fecha < hoy,
        laborable: c.laborable !== false,
      };
    });
    const total = datos.reduce((a, d) => a + d.valor, 0);
    const diasLaborables = datos.filter((d) => d.laborable).length;
    const media = diasLaborables ? total / diasLaborables : 0;
    const top = datos.reduce<(typeof datos)[number] | null>(
      (b, d) => (d.valor > (b?.valor ?? 0) ? d : b),
      null
    );
    const restantes = datos
      .filter((d) => !d.pasado)
      .reduce((a, d) => a + d.valor, 0);
    const hayFuturo = datos.some((d) => !d.pasado);
    const delta =
      totalSemanaAnterior > 0
        ? ((total - totalSemanaAnterior) / totalSemanaAnterior) * 100
        : null;
    return { datos, total, media, top, restantes, hayFuturo, delta };
  }, [dias, totalSemanaAnterior]);

  const tituloSemana =
    offset === 0
      ? t('dashboardCharts.thisWeek')
      : offset === -1
      ? t('dashboardCharts.lastWeek')
      : offset === 1
      ? t('dashboardCharts.nextWeek')
      : rangoTexto(lunes);

  const TooltipSemana = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const fecha = d.fecha
      ? aFecha(d.fecha).toLocaleDateString('es-ES', {
          day: 'numeric',
          month: 'long',
        })
      : '';
    return (
      <div
        style={{
          background: colors.tooltipBg,
          border: `1px solid ${colors.tooltipBorder}`,
          color: colors.tooltipText,
        }}
        className="rounded-xl px-3 py-2 text-xs shadow-lg backdrop-blur"
      >
        <div className="font-semibold">
          {DIAS[d.dia] ?? d.dia} {fecha && `· ${fecha}`}
        </div>
        <div className="mt-0.5">
          {d.laborable ? (
            <>
              <strong className="tabular-nums">{d.valor}</strong>{' '}
              {t('dashboardCharts.appointments')}
            </>
          ) : (
            <span className="opacity-70">{t('dashboardCharts.closed')}</span>
          )}
        </div>
      </div>
    );
  };

  const TickDia = ({ x, y, payload, index }: any) => {
    const d = semana.datos[index];
    const activo = d?.esHoy;
    const cerrado = d && !d.laborable;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          textAnchor="middle"
          dy={12}
          fontSize={isMobile ? 10 : 11}
          fontWeight={activo ? 700 : 500}
          fill={activo ? colors.profit : colors.tickFill}
          opacity={cerrado ? 0.45 : 1}
          textDecoration={cerrado ? 'line-through' : undefined}
        >
          {activo ? t('dashboardCharts.today') : payload.value}
        </text>
        {d?.numero != null && (
          <text textAnchor="middle" dy={26} fontSize={10} fill={colors.tickFill} opacity={0.7}>
            {d.numero}
          </text>
        )}
      </g>
    );
  };

  const deltaPositivo = (semana.delta ?? 0) >= 0;

  return (
    <Card
      className={`relative isolate overflow-hidden rounded-2xl ${GLASS_CARD} tour-chart-semana transition-shadow duration-300 hover:shadow-[0_12px_32px_-12px_rgba(16,50,84,0.35)] dark:hover:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.6)]`}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/15"
      />
      <CardContent className="p-4 sm:p-6 flex flex-col h-full">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-[0.06em]">
            {t('dashboardCharts.weekAppointments')}
          </h2>
          <div className="flex items-center gap-0.5 rounded-xl bg-gray-100/80 dark:bg-white/5 p-0.5">
            <BotonNav onClick={() => setOffset((o) => o - 1)} label={t('dashboardCharts.lastWeek')}>
              <ChevronLeft className="w-4 h-4" />
            </BotonNav>
            <button
              onClick={() => setOffset(0)}
              disabled={offset === 0}
              className="px-2 min-w-[96px] flex flex-col items-center leading-tight text-gray-700 dark:text-gray-200 disabled:cursor-default enabled:hover:text-[#2170b8]"
              title={offset !== 0 ? t('dashboardCharts.backToThisWeek') : undefined}
            >
              <span className="text-[11px] font-semibold">{tituloSemana}</span>
              {lunes && tituloSemana !== rangoTexto(lunes) && (
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  {rangoTexto(lunes)}
                </span>
              )}
            </button>
            <BotonNav onClick={() => setOffset((o) => o + 1)} label={t('dashboardCharts.nextWeek')}>
              <ChevronRight className="w-4 h-4" />
            </BotonNav>
          </div>
        </div>

        <div className="mt-2 flex items-end justify-between gap-3">
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-white tabular-nums">
                {semana.total}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('dashboardCharts.appointments')}
              </span>
            </div>
            <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
              {semana.delta === null ? (
                t('dashboardCharts.noPrevWeek')
              ) : (
                <span
                  className={`inline-flex items-center gap-0.5 font-semibold ${
                    deltaPositivo
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {deltaPositivo ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  {deltaPositivo ? '+' : ''}
                  {semana.delta.toFixed(0)}%
                  <span className="font-normal text-gray-400 dark:text-gray-500">
                    {t('dashboardCharts.vsPrevWeek')}
                  </span>
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            {semana.top && (
              <Chip icon={<Flame className="w-3 h-3 text-orange-500" />}>
                {DIAS[semana.top.dia] ?? semana.top.dia}
              </Chip>
            )}
            {semana.hayFuturo && (
              <Chip icon={<CalendarCheck className="w-3 h-3 text-emerald-500" />}>
                {t('dashboardCharts.pending', { count: semana.restantes })}
              </Chip>
            )}
          </div>
        </div>

        <div className="relative flex-1 min-h-[13rem] mt-4">
          <AnimatePresence>
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-10 rounded-xl bg-white/50 dark:bg-zinc-900/50 backdrop-blur-[1px]"
              />
            )}
          </AnimatePresence>
          <ResponsiveContainer>
            <BarChart data={semana.datos} margin={{ top: 18, right: 4, left: -18, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={colors.grid} />
              <XAxis
                dataKey="dia"
                interval={0}
                axisLine={false}
                tickLine={false}
                height={34}
                tick={<TickDia />}
              />
              <YAxis
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                tick={{ fill: colors.tickFill, fontSize: 10 }}
              />
              <Tooltip content={<TooltipSemana />} cursor={{ fill: colors.cursor }} />
              {semana.media > 0 && (
                <ReferenceLine
                  y={semana.media}
                  stroke={colors.tickFill}
                  strokeDasharray="4 4"
                  strokeOpacity={0.6}
                  label={
                    isMobile
                      ? undefined
                      : {
                          value: t('dashboardCharts.dailyAvg', {
                            valor: semana.media.toLocaleString('es-ES', {
                              maximumFractionDigits: 1,
                            }),
                          }),
                          position: 'insideTopRight',
                          fill: colors.tickFill,
                          fontSize: 10,
                        }
                  }
                />
              )}
              <Bar key={lunes} dataKey="valor" radius={[8, 8, 0, 0]} maxBarSize={42}>
                {semana.datos.map((d) => (
                  <Cell
                    key={d.fecha || d.dia}
                    fill={d.esHoy ? colors.profit : colors.bar}
                    fillOpacity={d.pasado ? 0.45 : 1}
                  />
                ))}
                <LabelList
                  dataKey="valor"
                  position="top"
                  fontSize={10}
                  fill={colors.tickFill}
                  formatter={(v: number) => (v > 0 ? v : '')}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardSemana;
